import { useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { themeColors } from 'colors';
import Button from 'domains/core/components/Button';

const boxContainerStyles = {
    flexGrow: '1',
    width: '100%',
    height: '100%',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: 2,
};
const imageStyles = {
    width: 'calc(100% - 2rem)',
    height: '12rem',
    position: 'absolute',
    padding: '1rem',
};
const typeContainerStyles = {
    flexGrow: '1',
    width: '100%',
    height: '100%',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'flex-end',
};
const backgroundStyles = {
    zIndex: '1',
    bottom: '0rem',
    left: '0rem',
    display: 'flex',
    width: 'calc(100% - 2.25rem)',
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '1.125rem 1.375rem',
    justifyContent: 'flex-end',
};

const renderButton = ({ isHovered, label, onClick }: { isHovered: boolean; label: string; onClick: () => void }) => {
    const buttonStyles = {
        background: themeColors.action.active,
        position: 'relative',
        bottom: '0.5rem',
        // Reveal-on-hover via opacity + pointer-events. The previous z-index:-100
        // approach pushed the button behind its own parent Stack's stacking context,
        // which made it unhittable by elementFromPoint (Playwright actionability,
        // touch devices, some a11y tooling) even when visible. opacity preserves
        // the hover-reveal UX while keeping the button in-band for hit testing
        // when revealed.
        // zIndex must be higher than both the title strip (zIndex: 1 in
        // `backgroundStyles`) and the hover-tint overlay (zIndex: 10 in
        // `overlayHoverStyles`). The overlay paints a 67%-opacity white wash;
        // anything below it renders desaturated. The strip below it swallows
        // clicks. Sitting above both keeps the button vivid and hittable.
        zIndex: 11,
        opacity: isHovered ? 1 : 0,
        pointerEvents: isHovered ? ('auto' as const) : ('none' as const),
        transition: 'opacity 150ms ease',
        '&:hover': {
            background: themeColors.action.selected,
        },
        width: '160px',
    };

    return (
        <Button
            color="secondary"
            data-testid={`start-building-${label.toLowerCase().replace(/\s+/g, '-')}`}
            key={label} // For list rendering
            onClick={onClick}
            size="medium"
            sx={buttonStyles}
            variant="contained"
        >
            {label}
        </Button>
    );
};

export type CurationItemProps = {
    imageSrc: any;
    onClick?: () => void;
    options?: { label: string; onClick: () => void }[];
    subtitle?: string;
    title: string;
    updateTag?: React.ReactNode;
    isAccessControlLayer?: boolean;
};

const CurationItem = ({
    imageSrc,
    onClick,
    options = [],
    subtitle,
    title,
    updateTag,
    isAccessControlLayer = false,
}: CurationItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const containerStyles = {
        position: 'relative',
        borderRadius: '6px',
        height: isAccessControlLayer ? '15rem' : '20rem',
        overflow: 'hidden',
        alignItems: 'flex-end',
        border: '0.125rem solid',
        borderColor: 'other.black',
        '&:hover': {
            cursor: options?.length ? 'auto' : 'pointer',
        },
    };

    const overlayHoverStyles = {
        zIndex: '10',
        flexGrow: '1',
        width: '100%',
        height: '100%',
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isHovered ? '#FFFFFFAA' : '#FFFFFF00',
        // Decorative hover-tint only — hover state is owned by the parent Stack
        // via onMouseEnter/onMouseLeave. Without pointer-events:none the overlay
        // intercepts every click meant for the buttons underneath (Email/SMS/
        // Voice/Direct Mail in Custom Curation, Start Building on the others).
        pointerEvents: 'none' as const,
    };

    const typeStyles = {
        color: isHovered ? 'action.active' : 'primary.main',
        fontSize: '1.5rem',
        paddingTop: '0rem',
        fontWeight: '500',
        lineHeight: '2rem',
    };

    const buttonOptions = options.length
        ? options.map(({ label, onClick }) => renderButton({ isHovered, label, onClick }))
        : renderButton({ isHovered, label: 'Start Building', onClick });

    return (
        <Stack
            sx={containerStyles}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
        >
            {updateTag}
            <Box sx={overlayHoverStyles} />
            <Box sx={imageStyles}>{imageSrc}</Box>
            {!isAccessControlLayer && <Stack sx={boxContainerStyles}>{buttonOptions}</Stack>}
            <Stack sx={typeContainerStyles}>
                <Stack sx={backgroundStyles}>
                    <Typography sx={typeStyles}>{title}</Typography>
                    <Typography variant="body2">{subtitle}</Typography>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default CurationItem;
